import { Close } from '@mui/icons-material';
import { Backdrop, Box, IconButton, Paper, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';

interface OnboardingSpotlightProps {
  open: boolean;
  onClose: () => void;
  targetSelector?: string;
  helperText: string;
  title: string;
}

export const OnboardingSpotlight: React.FC<OnboardingSpotlightProps> = ({
  open,
  onClose,
  targetSelector,
  helperText,
  title,
}) => {
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (open && targetSelector) {
      // Wait for DOM to be ready and poll for element
      const checkForElement = () => {
        const element = document.querySelector(targetSelector);
        if (element) {
          const rect = element.getBoundingClientRect();
          setTargetRect(rect);

          // Add click listener to close spotlight when target is clicked
          const handleTargetClick = () => {
            onClose();
          };
          element.addEventListener('click', handleTargetClick);

          // Cleanup
          return () => {
            element.removeEventListener('click', handleTargetClick);
          };
        } else {
          // Element not found, don't show overlay
          setTargetRect(null);
        }
      };

      const timer = setTimeout(checkForElement, 100);

      return () => clearTimeout(timer);
    } else {
      setTargetRect(null);
    }
  }, [open, targetSelector, onClose]);

  if (!open || !targetRect) return null;

  return (
    <Backdrop
      open={open}
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 1,
        backgroundColor: 'transparent',
        pointerEvents: 'none', // Let clicks pass through by default
      }}
    >
      {/* Single overlay with SVG mask cutout */}
      {targetRect && (
        <>
          {/* Four-panel overlay */}
          {/* Top overlay */}
          <Box
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: targetRect.top - 8,
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              pointerEvents: 'auto',
              cursor: 'pointer',
            }}
          />

          {/* Right overlay */}
          <Box
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: targetRect.top - 8,
              left: targetRect.right + 8,
              right: 0,
              height: targetRect.height + 16,
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              pointerEvents: 'auto',
              cursor: 'pointer',
            }}
          />

          {/* Bottom overlay */}
          <Box
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: targetRect.bottom + 8,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              pointerEvents: 'auto',
              cursor: 'pointer',
            }}
          />

          {/* Left overlay */}
          <Box
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: targetRect.top - 8,
              left: 0,
              width: targetRect.left - 8,
              height: targetRect.height + 16,
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              pointerEvents: 'auto',
              cursor: 'pointer',
            }}
          />

          {/* Border around highlighted area */}
          <Box
            sx={{
              position: 'absolute',
              top: targetRect.top - 8,
              left: targetRect.left - 8,
              width: targetRect.width + 16,
              height: targetRect.height + 16,
              borderRadius: 2,
              border: '3px solid',
              borderColor: 'primary.main',
              pointerEvents: 'none',
              backgroundColor: 'transparent',
            }}
          />
        </>
      )}

      {/* Helper text card */}
      <Paper
        onClick={(e) => e.stopPropagation()}
        sx={{
          position: 'absolute',
          ...(targetRect
            ? {
                top: Math.min(targetRect.bottom + 20, window.innerHeight - 300),
                left: Math.max(16, Math.min(targetRect.left, window.innerWidth - 416)),
                transform: 'none',
              }
            : {
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }),
          maxWidth: 400,
          p: 3,
          zIndex: (theme) => theme.zIndex.modal + 2,
          pointerEvents: 'auto',
        }}
      >
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          <IconButton size="small" onClick={onClose} sx={{ ml: 1 }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {helperText}
        </Typography>
      </Paper>
    </Backdrop>
  );
};

export default OnboardingSpotlight;
